import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Store, Info, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { authFetch } from '@/lib/authFetch';
import { createListing, updateListing, draftListingDescription } from '@/lib/marketplace';
import { MARKETPLACE, sellerShare, usd, percent } from '@/config/earnings';

// ─── Sell a site ──────────────────────────────────────────────────────────────
// List one of your sites on the marketplace, or edit a listing. Opened from a
// site's dashboard, from a card in My sites, or from the marketplace itself
// (which has no site in hand, so this asks which one).
//
// The form says what the seller keeps before they commit to a price: the
// commission is the first question anyone asks about a marketplace.
//
// A new listing opens with its description already written from the site
// (POST /api/marketplace/describe), and the seller edits it like any field.
// The draft never lands on top of what they have typed, and an edit is never
// drafted: a changed description sends a live listing back to review.
//
// Every listing is sold to any number of buyers. The form used to ask, with
// "One buyer only" as the other answer; older listings keep what they chose.

const FIELD = 'w-full rounded-xl border border-zinc-700 bg-zinc-950 px-3.5 text-sm text-white placeholder-zinc-600 outline-none focus:border-orange-500';
const LABEL = 'block text-xs font-semibold text-zinc-400 mb-1.5';

/** Sites with something in them — an empty first build has nothing to sell. */
function hasContent(site) {
  return String(site?.html_content || '').trim().length > 0 || site?.status === 'completed';
}

export default function SellSiteDialog({ open, onClose, site = null, listing = null, onSaved = null }) {
  const editing = !!listing;
  const [sites, setSites] = useState(null);
  const [websiteId, setWebsiteId] = useState(site?.id || listing?.website_id || '');
  const [title, setTitle] = useState(listing?.title || site?.title || '');
  const [description, setDescription] = useState(listing?.description || '');
  const [drafting, setDrafting] = useState(false);
  const [fromDraft, setFromDraft] = useState(false);
  const [touched, setTouched] = useState(false);
  // The same as `touched`, for the draft's callback: it outlives the render
  // that started it, and must not land on anything typed since.
  const touchedRef = useRef(false);
  const [category, setCategory] = useState(listing?.category || 'business');
  const [price, setPrice] = useState(listing ? String(listing.price_usd) : '29');
  const [refreshContent, setRefreshContent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset to whatever this opening is for. Keyed on ids, not objects, so a
  // parent re-rendering with a fresh object does not wipe what was typed.
  useEffect(() => {
    if (!open) return;
    setWebsiteId(site?.id || listing?.website_id || '');
    setTitle(listing?.title || site?.title || '');
    setDescription(listing?.description || '');
    touchedRef.current = false;
    setTouched(false);
    setFromDraft(false);
    setCategory(listing?.category || 'business');
    setPrice(listing ? String(listing.price_usd) : '29');
    setRefreshContent(false);
    setError('');
  }, [open, site?.id, listing?.id]);

  // Write the description from the site, for a new listing. Choosing another
  // site in the picker writes one for that site instead, unless the seller
  // has started on their own.
  useEffect(() => {
    if (!open || editing || !websiteId) return undefined;
    let cancelled = false;
    setDrafting(true);
    // Started inside a promise, so even a failure to send ends up in .catch:
    // drafting is a convenience and must never stop the form opening.
    Promise.resolve()
      .then(() => draftListingDescription(websiteId))
      .then((res) => {
        const draft = typeof res?.description === 'string' ? res.description.trim() : '';
        if (cancelled || touchedRef.current || !draft) return;
        setDescription(draft.slice(0, 2000));
        setFromDraft(true);
      })
      .catch(() => { /* the seller writes their own, as they always could */ })
      .finally(() => { if (!cancelled) setDrafting(false); });
    return () => { cancelled = true; setDrafting(false); };
  }, [open, editing, websiteId]);

  const editDescription = (value) => {
    touchedRef.current = true;
    setTouched(true);
    setFromDraft(false);
    setDescription(value);
  };
  const writing = drafting && !touched;

  // No site in hand: offer the account's finished sites.
  useEffect(() => {
    if (!open || site || editing) return undefined;
    let cancelled = false;
    authFetch('/api/websites')
      .then(({ websites }) => { if (!cancelled) setSites((websites || []).filter(hasContent)); })
      .catch(() => { if (!cancelled) setSites([]); });
    return () => { cancelled = true; };
  }, [open, site, editing]);

  const priceNum = Number(price);
  const priceOk = Number.isFinite(priceNum) && priceNum >= MARKETPLACE.minPriceUsd && priceNum <= MARKETPLACE.maxPriceUsd;
  const earn = useMemo(() => (priceOk ? sellerShare(priceNum) : null), [priceOk, priceNum]);

  const chosen = sites?.find(s => s.id === websiteId);
  useEffect(() => {
    if (chosen && !title) setTitle(chosen.title || '');
  }, [chosen, title]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!editing && !websiteId) { setError('Choose which site to sell.'); return; }
    if (title.trim().length < 3) { setError('Give the listing a title of at least 3 characters.'); return; }
    if (!priceOk) { setError(`Set a price between ${usd(MARKETPLACE.minPriceUsd)} and ${usd(MARKETPLACE.maxPriceUsd)}.`); return; }

    setSaving(true);
    try {
      const body = { title: title.trim(), description: description.trim(), category, price: priceNum };
      const { listing: saved } = editing
        ? await updateListing(listing.id, { ...body, ...(refreshContent ? { refreshContent: true } : {}) })
        : await createListing({ ...body, websiteId });
      toast.success(
        saved.status === 'active'
          ? 'Your site is on the marketplace.'
          : editing ? 'Listing updated — it will be reviewed again before it shows.' : 'Listing sent for review — we will let you know when it is live.',
      );
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(err.message || 'Could not save the listing.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent className="bg-zinc-900 border border-zinc-800 text-white max-w-lg w-[calc(100vw-2rem)] rounded-2xl p-0 overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 border-b border-zinc-800 flex items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-orange-500/15 grid place-items-center shrink-0">
            <Store className="w-5 h-5 text-orange-400" />
          </span>
          <div>
            <DialogTitle className="text-white text-lg font-bold">{editing ? 'Edit your listing' : 'Sell this site'}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm mt-0.5">
              Buyers get their own copy with the full code. You keep your original.
            </DialogDescription>
          </div>
        </div>

        {/* noValidate: the browser's own "value must be at least 5" bubble
            would stop the submit before the clearer message below is shown. */}
        <form onSubmit={submit} noValidate className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {!site && !editing && (
            <div>
              <label htmlFor="sell-site" className={LABEL}>Site</label>
              {sites === null ? (
                <div className="flex items-center gap-2 text-sm text-zinc-500 h-11"><Loader2 className="w-4 h-4 animate-spin" /> Loading your sites…</div>
              ) : sites.length === 0 ? (
                <p className="text-sm text-zinc-500">Build a site first — finished sites can be listed here.</p>
              ) : (
                <select id="sell-site" value={websiteId} onChange={(e) => setWebsiteId(e.target.value)} className={`${FIELD} h-11`}>
                  <option value="">Choose a site…</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.title || 'Untitled site'}</option>)}
                </select>
              )}
            </div>
          )}

          <div>
            <label htmlFor="sell-title" className={LABEL}>Listing title</label>
            <input id="sell-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160}
              placeholder="e.g. Modern bakery site with online menu" className={`${FIELD} h-11`} />
          </div>

          <div>
            <div className="flex items-center justify-between gap-3 mb-1.5">
              <label htmlFor="sell-description" className="text-xs font-semibold text-zinc-400">Description</label>
              {writing && (
                <span role="status" className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500">
                  <Loader2 className="w-3 h-3 animate-spin" /> Writing it from your site…
                </span>
              )}
            </div>
            <textarea id="sell-description" value={description} onChange={(e) => editDescription(e.target.value)} maxLength={2000} rows={6}
              aria-busy={writing}
              placeholder={writing ? 'Writing a description from your site…' : 'Who it is for, the pages it has, what makes it stand out.'}
              className={`${FIELD} py-2.5 resize-none leading-relaxed`} />
            <div className="mt-1 flex items-start justify-between gap-3">
              <p className="text-[11px] text-zinc-500">{fromDraft ? 'Written from your site. Change anything you like.' : ''}</p>
              <p className="text-[11px] text-zinc-600 tabular-nums shrink-0">{description.length} / 2000</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="sell-category" className={LABEL}>Category</label>
              <select id="sell-category" value={category} onChange={(e) => setCategory(e.target.value)} className={`${FIELD} h-11`}>
                {MARKETPLACE.categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="sell-price" className={LABEL}>Price (USD)</label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-zinc-500">$</span>
                <input id="sell-price" type="number" inputMode="decimal" min={MARKETPLACE.minPriceUsd} max={MARKETPLACE.maxPriceUsd} step="1"
                  value={price} onChange={(e) => setPrice(e.target.value)} className={`${FIELD} h-11 pl-7`} />
              </div>
            </div>
          </div>

          {editing && listing?.website_id && (
            <label className="flex items-start gap-2.5 rounded-xl border border-zinc-800 p-3 cursor-pointer">
              <input type="checkbox" checked={refreshContent} onChange={(e) => setRefreshContent(e.target.checked)} className="mt-0.5 accent-orange-500" />
              <span>
                <span className="flex items-center gap-1.5 text-sm font-medium text-white"><RefreshCw className="w-3.5 h-3.5" /> Update the listing from my site</span>
                <span className="block text-xs text-zinc-500 mt-0.5">Buyers get your site as it is now. The listing is reviewed again.</span>
              </span>
            </label>
          )}

          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.06] p-3.5 flex items-start gap-2.5">
            <Info className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-100/90 leading-relaxed">
              {earn != null
                ? <>You earn <span className="font-semibold text-white">{usd(earn)}</span> per sale after BuildersLab’s {percent(MARKETPLACE.commissionRate)} commission. </>
                : <>You keep {percent(1 - MARKETPLACE.commissionRate)} of every sale. </>}
              Earnings can be paid out {MARKETPLACE.holdDays} days after each sale.
              {!MARKETPLACE.autoApprove && ' New listings are reviewed before they appear.'}
            </p>
          </div>

          {error && <p role="alert" className="text-sm text-red-400">{error}</p>}

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} disabled={saving}
              className="flex-1 h-11 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-sm font-semibold text-zinc-200">
              Cancel
            </button>
            <button type="submit" disabled={saving || (!editing && !site && !sites?.length)}
              className="flex-1 h-11 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-sm font-semibold text-ink inline-flex items-center justify-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {editing ? 'Save listing' : 'List for sale'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
